// src/ingest/labor.ts — live labor-side ingest, built on the SAME FRED path as
// fred.ts (plan §5: extend the existing framework, don't fork it). Pure parse +
// freshness logic here; the network fetch is injected and reused from fred.ts so
// tests never touch the wire. Every field returns a provenance-carrying value and
// a single-series failure NEVER throws — it keeps the last-good seed flagged
// STALE (INV-5 / INV-E5).

import { fetchSeriesLatest } from "./fred.ts";
import type { ObservationRow } from "./run.ts";
import { LABOR_SHARE_ANCHOR, coldStartBaseline } from "../econ/baseline.ts";
import type { EconBaseline, Field } from "../econ/types.ts";

type FetchLike = (url: string) => Promise<{ ok: boolean; status: number; json: () => Promise<unknown> }>;

export type Cadence = "daily" | "monthly" | "quarterly" | "annual";

/** Freshness window per cadence, in days. Past it → STALE even if the fetch
 *  succeeded (honest cadence: old data is stale data). Plan §5.
 *
 *  Windows are measured from the OBSERVATION date and are release-lag-aware: the
 *  freshest monthly series is already ~30–45 days old when published (and ages a
 *  further month before the next print), so the window must clear a healthy,
 *  freshest-available value and only trip when a feed has actually stopped
 *  updating — roughly two publication cycles. */
export const FRESHNESS_DAYS: Record<Cadence, number> = {
  daily: 4,
  monthly: 70,
  // FRED dates quarterly series by the period-START day (Q2 → Apr 1), so the
  // freshest value is already ~180 days old by observation date; 220 d clears a
  // current quarter but still trips when a quarter's release is actually missed.
  quarterly: 220,
  annual: 430,
};

/** True if an observation dated `asOf` is older than its cadence window at `now`. */
export function isStale(asOf: string, now: string, cadence: Cadence): boolean {
  const a = Date.parse(asOf);
  const n = Date.parse(now);
  if (!Number.isFinite(a) || !Number.isFinite(n)) return true;
  const days = (n - a) / 86_400_000;
  return days > FRESHNESS_DAYS[cadence];
}

interface SeriesDef {
  series: string;
  key: keyof EconBaseline;
  cadence: Cadence;
  unit: string;
  /** Convert the raw FRED value to the EconBaseline unit (shares as 0..1). */
  convert: (raw: number) => number;
  title: string;
}

/** FRED series → EconBaseline field. One table, same shape as fred.ts's maps. */
export const LABOR_SERIES: SeriesDef[] = [
  { series: "GDPC1", key: "gdp", cadence: "quarterly", unit: "$B", convert: (v) => v, title: "Real GDP (chained 2017 $B)" },
  { series: "UNRATE", key: "unemployment", cadence: "monthly", unit: "share", convert: (v) => v / 100, title: "Unemployment rate" },
  { series: "PRS85006173", key: "labor_share", cadence: "quarterly", unit: "share", convert: (v) => LABOR_SHARE_ANCHOR * (v / 100), title: "Nonfarm business labor share (index→share)" },
  { series: "PAYEMS", key: "employment", cadence: "monthly", unit: "thousands", convert: (v) => v, title: "Total nonfarm employment" },
  { series: "JTSJOL", key: "openings", cadence: "monthly", unit: "thousands", convert: (v) => v, title: "Job openings (JOLTS)" },
  { series: "JTSHIL", key: "hires", cadence: "monthly", unit: "thousands", convert: (v) => v, title: "Hires (JOLTS)" },
  { series: "JTSTSL", key: "separations", cadence: "monthly", unit: "thousands", convert: (v) => v, title: "Total separations (JOLTS)" },
];

const round6 = (x: number): number => Math.round(x * 1e6) / 1e6;

export interface LaborIngestResult {
  econ: EconBaseline;
  observations: ObservationRow[];
  errors: Array<{ series: string; error: string }>;
}

/**
 * Build the live EconBaseline. Starts from the cold-start seed and upgrades each
 * FRED-sourced field to LIVE on success (or STALE if the observation is beyond
 * its freshness window). A missing key or any per-series failure leaves that
 * field on its seed value flagged STALE — never a silent stale.
 */
export async function ingestLabor(opts: {
  fredKey?: string;
  now: string;
  fetchImpl?: FetchLike;
}): Promise<LaborIngestResult> {
  const { fredKey, now } = opts;
  const fetchImpl = opts.fetchImpl ?? ((url: string) => fetch(url));
  const econ = coldStartBaseline();
  const observations: ObservationRow[] = [];
  const errors: Array<{ series: string; error: string }> = [];

  for (const def of LABOR_SERIES) {
    const prior = econ[def.key] as Field;
    try {
      if (!fredKey) throw new Error("FRED_API_KEY missing");
      const o = await fetchSeriesLatest(def.series, fredKey, fetchImpl);
      if (!o) throw new Error("no numeric observation");
      const value = def.convert(o.value);
      if (!Number.isFinite(value)) throw new RangeError(`non-finite value for ${def.series}`);
      const stale = isStale(o.date, now, def.cadence);
      econ[def.key] = {
        ...prior,
        value: round6(value),
        flag: stale ? "STALE" : "LIVE",
        source: "FRED:" + def.series,
        as_of: o.date,
        fetched_at: now,
        note: def.title + (stale ? " (beyond freshness window)" : ""),
      };
      observations.push({ source: "fred", series: def.series, period: o.date, value: round6(o.value), unit: def.unit });
    } catch (e) {
      econ[def.key] = { ...prior, flag: "STALE", fetched_at: now };
      errors.push({ series: def.series, error: String(e instanceof Error ? e.message : e) });
    }
  }

  return { econ, observations, errors };
}
